import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import Cookies from 'js-cookie';

const options = {
  legend: { show: false, position: 'top', horizontalAlign: 'left' },
  colors: ['#3C50E0', '#80CAEE'],
  chart: {
    fontFamily: 'Satoshi, sans-serif',
    height: 335,
    type: 'area',
    toolbar: { show: false },
  },
  stroke: { width: [2, 2], curve: 'straight' },
  grid: {
    xaxis: { lines: { show: true } },
    yaxis: { lines: { show: true } },
  },
  dataLabels: { enabled: false },
  markers: {
    size: 4,
    colors: '#fff',
    strokeColors: ['#3056D3', '#80CAEE'],
    strokeWidth: 3,
  },
  xaxis: {
    type: 'datetime',
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: {
      datetimeUTC: false, 
      format: 'MMM \'yy',
    },
  },
  yaxis: { min: 0 },
};

const ChartOne = ({googlePropertyId}) => {
  const [chartData, setChartData] = useState({ series: [], categories: [] });
  const [selectedRange, setSelectedRange] = useState('last3months');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const dateRanges = {
    lastweek: 7,
    lastmonth: 30,
    last3months: 90,
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const adminToken = Cookies.get('adminKey');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}analytics/views?daysAgo=${dateRanges[selectedRange]}&propertyId=${googlePropertyId}`, {
            method: 'GET',
            headers: {
              "Admin-Token": adminToken
            }
          });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
        const dateMap = new Map();
  
        const dateSet = new Set();
  
        let minDate = Infinity;
        let maxDate = -Infinity;
  
        data.rows.forEach((row) => {
            const [day, month, year] = row[1].split('-');
            const date = new Date(year, month - 1, day).getTime();
            dateMap.set(date, parseInt(row[2], 10));
            dateSet.add(date);
            
            if (date < minDate) minDate = date;
            if (date > maxDate) maxDate = date;
        });
        
        const categories = [];
        const pageviews = [];
  
        // Generate series data from date range
        let currentDate = minDate;
        while (currentDate <= maxDate) {
          categories.push(currentDate);
          pageviews.push(dateMap.get(currentDate) || 0);
          currentDate += 24 * 60 * 60 * 1000; // Add one day in milliseconds
        }
  
        setChartData({
          series: [{ name: 'Visualizzazioni', data: pageviews }],
          categories: categories,
        });
  
      } catch (error) {
        console.error('Error fetching analytics data', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [selectedRange]);


  if (loading) {
    return (
      <div className="col-span-12 rounded-2xl border border-stroke bg-white px-5 pt-7.5 pb-5 shadow-default">
        <div className='inline-flex space-x-4 items-center'>
          <h1 className="text-xl text-black dark:text-white">Loading Chart Data</h1>
          <svg className="animate-spin h-5 w-5 mx-auto text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.961 7.961 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <div>
          <div id="chartOne" className="-ml-5">
            <ReactApexChart
              options={options}
              series={[]} // Empty series data
              type="area"
              height={350}
            />
          </div>
        </div>
      </div>
    ); 
  }


  if (error) {
    return (
      <div className="col-span-12 rounded-2xl border border-stroke bg-white px-5 pt-7.5 pb-5 shadow-default">
        {/* ... other elements above the chart (if any) ... */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '350px' }}> {/* Center error message */}
          <span className="text-red-500 font-semibold">Impossibile Caricare i dati</span>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-12 rounded-2xl border border-stroke bg-white px-5 pt-7.5 pb-5 shadow-default">
      <div className="flex flex-row items-start justify-between gap-3 pb-5">
        <div className="flex w-full flex-wrap gap-3">
          <div className="flex min-w-47.5">
            <span className="mt-1 mr-2 flex h-4 w-full max-w-4 items-center justify-center rounded-full border border-primary">
              <span className="block h-2.5 w-full max-w-2.5 rounded-full bg-primary"></span>
            </span>
            <div className="w-full">
              <p className="font-semibold text-primary">Visualizzazioni</p>
              <p className="text-sm font-medium">Ultimi {dateRanges[selectedRange]} Giorni</p>
            </div>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="flex gap-3 w-full">
          <button
            className={`px-4 py-2 rounded-lg ${selectedRange === 'lastweek' ? 'bg-primary text-white' : 'bg-gray-200'}`}
            onClick={() => setSelectedRange('lastweek')}
          >
            Scorsa Settimana
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${selectedRange === 'lastmonth' ? 'bg-primary text-white' : 'bg-gray-200'}`}
            onClick={() => setSelectedRange('lastmonth')}
          >
            Scorso Mese
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${selectedRange === 'last3months' ? 'bg-primary text-white' : 'bg-gray-200'}`}
            onClick={() => setSelectedRange('last3months')}
          >
            Ultimi 3 Mesi
          </button>
        </div>
      </div>

      <div>
        <div id="chartOne" className="-ml-5">
          <ReactApexChart
            options={{ ...options, xaxis: { type: 'datetime', categories: chartData.categories } }}
            series={chartData.series}
            type="area"
            height={350}
          />
        </div>
      </div>
    </div>
  );
};

export default ChartOne;